import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { LogoLight } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LeadLog from "./admin/LeadLog";
import OperatorsPanel from "./admin/Operators";
import StatsPanel from "./admin/Stats";
import { SignOut, TestTube } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [islands, setIslands] = useState([]);
  const [island, setIsland] = useState("aruba");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // If we have a user passed from AuthCallback, skip the check briefly.
    if (location.state?.user) return;
    if (loading) return;
    if (!user || !user.is_admin) navigate("/login", { replace: true });
  }, [user, loading, navigate, location.state]);

  useEffect(() => {
    api.get("/islands", { params: { active_only: true } }).then((r) => setIslands(r.data)).catch(() => setIslands([{ slug: "aruba", name: "Aruba" }]));
  }, []);

  const triggerTestLead = async () => {
    try {
      await api.post(`/admin/leads/test?island_slug=${island}`);
      toast.success("Simulated lead created");
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create test lead");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="font-display text-2xl text-navy">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-cream2 bg-cream sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-6">
          <Link to="/"><LogoLight /></Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50">Signed in</div>
              <div className="text-sm text-navy font-medium" data-testid="admin-user-email">{user.email}</div>
            </div>
            {user.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full ring-1 ring-gold/40" />}
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-navy/30 px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-navy hover:bg-navy hover:text-cream transition-colors"
            >
              <SignOut size={16} weight="thin" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-teal mb-2">Control room</div>
            <h1 className="font-display text-4xl sm:text-5xl text-navy leading-none">LocalRida Ops</h1>
          </div>
          <div className="flex items-end gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50 mb-1">Island</div>
              <Select value={island} onValueChange={setIsland}>
                <SelectTrigger className="w-[180px] rounded-none border-navy/20 bg-cream" data-testid="island-selector">
                  <SelectValue placeholder="Island" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {islands.map((i) => (
                    <SelectItem key={i.slug} value={i.slug} data-testid={`island-option-${i.slug}`}>
                      {i.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              onClick={triggerTestLead}
              data-testid="admin-test-lead-button"
              className="inline-flex items-center gap-2 bg-teal text-cream px-4 py-2 text-[11px] uppercase tracking-[0.24em] hover:bg-teal-bright transition-colors"
            >
              <TestTube size={16} weight="thin" /> Simulate Lead
            </button>
          </div>
        </div>

        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="bg-transparent p-0 gap-8 border-b border-cream2 rounded-none h-auto justify-start">
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-gold data-[state=active]:border-b-2 data-[state=active]:border-gold px-0 pb-3 text-[11px] uppercase tracking-[0.24em]">
              Lead Log
            </TabsTrigger>
            <TabsTrigger value="operators" data-testid="tab-operators" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-gold data-[state=active]:border-b-2 data-[state=active]:border-gold px-0 pb-3 text-[11px] uppercase tracking-[0.24em]">
              Operators
            </TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-gold data-[state=active]:border-b-2 data-[state=active]:border-gold px-0 pb-3 text-[11px] uppercase tracking-[0.24em]">
              Stats
            </TabsTrigger>
          </TabsList>
          <TabsContent value="leads" className="pt-8">
            <LeadLog island={island} reloadKey={reloadKey} />
          </TabsContent>
          <TabsContent value="operators" className="pt-8">
            <OperatorsPanel island={island} onChange={() => setReloadKey((k) => k + 1)} />
          </TabsContent>
          <TabsContent value="stats" className="pt-8">
            <StatsPanel island={island} reloadKey={reloadKey} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
